// Keeping a moving product in the room whatever the room's shape. The room's
// walls are solid (WallFacts.solidBBoxes), so a product moving towards a
// target stops flush against any wall in its way and slides along it, as it
// butts against another product (buttTowards). Nothing here knows whether
// the room is rectangular or L-shaped, or which wall is which.
import * as THREE from 'three';
import {overlapOn} from 'components/customer/RoomPlanner/helpers/boxes';
import {
    BUTT_TOLERANCE,
    buttTowards,
} from 'components/customer/RoomPlanner/helpers/movement/butting';

// How many times a product is pushed out of a wall it starts in, or butted
// on towards its target: enough to clear a corner where two walls meet, or
// to round an inside corner in one move.
const TRIES = 3;

/**
 * @param {THREE.Box3} solid One of a product's solid boxes.
 * @param {THREE.Box3} wall A wall's solid box.
 * @return {boolean} True when it is in the wall by more than touching.
 */
const inWall = (solid: THREE.Box3, wall: THREE.Box3): boolean =>
    [0, 1, 2].every((axis) => overlapOn(solid, wall, axis) > BUTT_TOLERANCE);

/**
 * @param {THREE.Box3[]} solids A product's solid boxes where it is.
 * @param {THREE.Box3[]} walls The walls' solid boxes.
 * @return {boolean} True when it is in no wall, flush against one included.
 * A product inside the room can't get past a wall, so this is all a move
 * from inside needs.
 */
export const clearOfWalls = (
    solids: readonly THREE.Box3[],
    walls: readonly THREE.Box3[]
): boolean =>
    !solids.some((solid) => walls.some((wall) => inWall(solid, wall)));

/**
 * @param {THREE.Box3} solid One of a product's solid boxes, in a wall.
 * @param {THREE.Box3} wall The wall's solid box.
 * @return {THREE.Vector3} The shortest move across the floor that takes it
 * out, to whichever side of the wall is nearer.
 */
const outOf = (solid: THREE.Box3, wall: THREE.Box3): THREE.Vector3 =>
    [0, 2]
        .flatMap((axis) => [
            new THREE.Vector3().setComponent(
                axis,
                wall.min.getComponent(axis) - solid.max.getComponent(axis)
            ),
            new THREE.Vector3().setComponent(
                axis,
                wall.max.getComponent(axis) - solid.min.getComponent(axis)
            ),
        ])
        .reduce((shortest, move) =>
            move.length() < shortest.length() ? move : shortest
        );

/**
 * Where a product gets to in the room on its way to a target: out of any wall
 * it starts in, as one the room has just turned to face a wall can, then as
 * far towards the target as the walls allow, flush against a wall in the way
 * and sliding along it, round an inside corner if the target is past one.
 * @param {THREE.Box3[]} solids The product's solid boxes where it is.
 * @param {THREE.Vector3} from Where it is.
 * @param {THREE.Vector3} to Where it is headed.
 * @param {THREE.Box3[]} walls The walls' solid boxes.
 * @return {THREE.Vector3} Where it gets to.
 */
export const keepInRoom = (
    solids: readonly THREE.Box3[],
    from: THREE.Vector3,
    to: THREE.Vector3,
    walls: readonly THREE.Box3[]
): THREE.Vector3 => {
    const position = from.clone();
    const moved = solids.map((solid) => solid.clone());
    const moveBy = (shift: THREE.Vector3) => {
        position.add(shift);
        moved.forEach((solid) => solid.translate(shift));
    };

    for (let i = 0; i < TRIES; i++) {
        const stuck = moved.flatMap((solid) =>
            walls
                .filter((wall) => inWall(solid, wall))
                .map((wall) => outOf(solid, wall))
        );
        if (stuck.length === 0) break;
        moveBy(stuck[0]);
    }

    // Each axis in turn, so a wall stopping one way leaves the others free;
    // again from where that got to, so a product blocked one way until
    // another took it past a corner carries on.
    for (let i = 0; i < TRIES; i++) {
        const reached = buttTowards(moved, position, to, walls);
        const shift = reached.clone().sub(position);
        if (shift.lengthSq() < 1e-12) break;
        moveBy(shift);
    }

    return position;
};
